if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.minNumberInput = this.querySelector('.price-range__number-input--min') || document.getElementById('price-range-number-min');
      this.maxNumberInput = this.querySelector('.price-range__number-input--max') || document.getElementById('price-range-number-max');
      this.minSliderInput = this.querySelector('.price-range__slider-input--min') || document.getElementById('price-range-slider-min');
      this.maxSliderInput = this.querySelector('.price-range__slider-input--max') || document.getElementById('price-range-slider-max');
      this.minGap = 10;

      if (!this.minNumberInput || !this.maxNumberInput || !this.minSliderInput || !this.maxSliderInput) {
        return;
      }

      this.minValue = Number(this.minNumberInput.min);
      this.maxValue = Number(this.maxNumberInput.max);
      this.timeout = null;

      this.handleInputBound = this.handleInput.bind(this);
      this.handleSliderInputBound = this.handleSliderInput.bind(this);
      this.handleSliderChangeBound = this.handleSliderChange.bind(this);

      this.addEventListener('input', this.handleInputBound);
      this.minSliderInput.addEventListener('input', this.handleSliderInputBound);
      this.maxSliderInput.addEventListener('input', this.handleSliderInputBound);
      this.minSliderInput.addEventListener('change', this.handleSliderChangeBound);
      this.maxSliderInput.addEventListener('change', this.handleSliderChangeBound);
    }

    /**
     * Handles 'input' events on the price range component.
     * @param {object} evt - Event object.
     */
    handleInput(evt) {
      if (evt.detail !== undefined) {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.updateSliderInputs(evt);
          this.updateNumberInputs();
        }, 500);
      } else {
        this.updateSliderInputs(evt);
        this.updateNumberInputs();
      }
    }

    /**
     * Handles direct slider input events.
     * Stops bubbling so live filter updates don't happen while dragging.
     * @param {object} evt
     */
    handleSliderInput(evt) {
      evt.stopPropagation();
      this.updateSliderInputs(evt);
      this.updateNumberInputs();
    }

    /**
     * Handles slider drag commit.
     * Applies same state sync and emits a number-input change to run final filter update.
     * @param {object} evt
     */
    handleSliderChange(evt) {
      evt.stopPropagation();
      this.handleSliderInput(evt);
      const target = evt.target === this.minSliderInput ? this.minNumberInput : this.maxNumberInput;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Updates the value of the 'number' type inputs.
     */
    updateNumberInputs() {
      this.minNumberInput.value = this.minSliderInput.value !== this.minNumberInput.min
        ? this.minSliderInput.value : null;

      this.maxNumberInput.value = this.maxSliderInput.value !== this.maxNumberInput.max
        ? this.maxSliderInput.value : null;
    }

    /**
     * Updates the value of the 'range' type inputs.
     * @param {object} evt - Event object.
     */
    updateSliderInputs(evt) {
      const parseOrDefault = (input, fallback) => {
        const value = parseInt(input.value, 10);
        return Number.isNaN(value) ? fallback : value;
      };

      let minTarget = parseInt(this.minSliderInput.value, 10);
      let maxTarget = parseInt(this.maxSliderInput.value, 10);

      if (evt.target === this.minNumberInput) {
        minTarget = parseOrDefault(this.minNumberInput, this.minValue);
        maxTarget = parseOrDefault(this.maxNumberInput, this.maxValue);
        if (minTarget > maxTarget - this.minGap) {
          maxTarget = Math.min(this.maxValue, minTarget + this.minGap);
        }
      } else if (evt.target === this.maxNumberInput) {
        minTarget = parseOrDefault(this.minNumberInput, this.minValue);
        maxTarget = parseOrDefault(this.maxNumberInput, this.maxValue);
        if (maxTarget < minTarget + this.minGap) {
          minTarget = Math.max(this.minValue, maxTarget - this.minGap);
        }
      } else if (evt.target === this.minSliderInput) {
        minTarget = parseInt(this.minSliderInput.value, 10);
        minTarget = Math.min(Math.max(minTarget, this.minValue), this.maxValue);
        if (minTarget > maxTarget - this.minGap) {
          minTarget = Math.max(this.minValue, maxTarget - this.minGap);
        }
      } else if (evt.target === this.maxSliderInput) {
        maxTarget = parseInt(this.maxSliderInput.value, 10);
        maxTarget = Math.min(Math.max(maxTarget, this.minValue), this.maxValue);
        if (maxTarget < minTarget + this.minGap) {
          maxTarget = Math.min(this.maxValue, minTarget + this.minGap);
        }
      }

      minTarget = Number.isNaN(minTarget) ? this.minValue : minTarget;
      maxTarget = Number.isNaN(maxTarget) ? this.maxValue : maxTarget;
      minTarget = Math.min(Math.max(minTarget, this.minValue), this.maxValue);
      maxTarget = Math.min(Math.max(maxTarget, this.minValue), this.maxValue);

      if (minTarget + this.minGap > maxTarget) {
        if (evt.target === this.minNumberInput || evt.target === this.minSliderInput) {
          minTarget = Math.max(this.minValue, maxTarget - this.minGap);
        } else {
          maxTarget = Math.min(this.maxValue, minTarget + this.minGap);
        }
      }

      this.minSliderInput.value = minTarget;
      this.maxSliderInput.value = maxTarget;

      this.updateNumberInputs();
    }

  }

  customElements.define('price-range', PriceRange);
}
